// 文件路径: src/components/common/PersonHeightControl.js
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from '../../i18n';
import averageHeights from '../../data/average_heights.json';
import defaultConfig from '../../config/td1501_params.json';
import { useUnit } from '../../contexts/UnitContext';
import { formatLength, getDisplayValue, getMetersFromInput } from '../../utils/unitConversion';

const PersonHeightControl = ({ personHeight, onPersonHeightChange, onPersonHeightInput, label, isAdmin = false }) => {
    const { t, language } = useTranslation();
    const { unitSystem, detectedCountry, isRegionFetched } = useUnit();
    const [heightSourceInfo, setHeightSourceInfo] = useState('');

    const [isHeightManuallySet, setIsHeightManuallySet] = useState(false);
    const [selectedCountryCode, setSelectedCountryCode] = useState('');

    const sortedCountries = useMemo(() => {
        return Object.entries(averageHeights)
            .map(([code, data]) => ({
                code,
                name: language === 'zh' ? data.name_zh : data.name_en
            }))
            .sort((a, b) => a.name.localeCompare(b.name, language === 'zh' ? 'zh-CN' : 'en-US'));
    }, [language]);

    useEffect(() => {
        if (!isAdmin && !isHeightManuallySet && isRegionFetched && detectedCountry) {
            const countryData = averageHeights[detectedCountry];
            if (countryData) {
                const newHeight = countryData.height + 0.05;
                if (onPersonHeightChange) {
                    onPersonHeightChange(newHeight);
                }
                setSelectedCountryCode(detectedCountry);
                const regionName = language === 'zh' ? countryData.name_zh : countryData.name_en;
                setHeightSourceInfo(t('regionAverageHeight', { region: regionName }));
            } else {
                setHeightSourceInfo(t('regionNotFound'));
            }
        } else if (!isAdmin && !isHeightManuallySet && isRegionFetched && !detectedCountry) {
            setHeightSourceInfo(t('regionNotFound'));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, language, t, isRegionFetched, detectedCountry]);


    useEffect(() => {
        if (isHeightManuallySet) {
            setSelectedCountryCode('');
            setHeightSourceInfo(t('manualAdjust'));
        }
    }, [isHeightManuallySet, t]);
    
    const handleSliderInput = (e) => {
        if (onPersonHeightInput) {
            const val = parseFloat(e.target.value);
            onPersonHeightInput(getMetersFromInput(val, unitSystem));
        }
    };

    const handleSliderChange = (e) => {
        setIsHeightManuallySet(true);
        if (onPersonHeightChange) {
            const val = parseFloat(e.target.value);
            onPersonHeightChange(getMetersFromInput(val, unitSystem));
        }
    };


    const handleCountrySelect = (countryCode) => {
        setSelectedCountryCode(countryCode);
        if (!countryCode) return;

        const countryData = averageHeights[countryCode];
        if (countryData) {
            const newHeight = countryData.height + 0.05;
            if (onPersonHeightChange) {
                onPersonHeightChange(newHeight);
            }
            
            const regionName = language === 'zh' ? countryData.name_zh : countryData.name_en;
            setHeightSourceInfo(t('regionAverageHeight', { region: regionName }));
            setIsHeightManuallySet(false);
        }
    };

    const displayHeight = personHeight !== undefined ? personHeight : defaultConfig.personHeight;

    const minVal = unitSystem === 'imperial' ? 3.2 : 1.0;
    const maxVal = unitSystem === 'imperial' ? 7.3 : 2.2;
    const stepVal = unitSystem === 'imperial' ? 0.1 : 0.05;

    return (
        <div>
            <label htmlFor="personHeightSlider" className="block text-sm font-medium text-gray-600 mb-1">
                {label || t('personHeight')}: <span className="font-bold text-cyan-600">{formatLength(displayHeight, unitSystem, 2)}</span>
            </label>
            <select 
                value={selectedCountryCode}
                onChange={(e) => handleCountrySelect(e.target.value)} 
                className="w-full text-xs p-1 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
            >
                <option value="">{t('selectCountry')}</option>
                {sortedCountries.map(country => (
                    <option key={country.code} value={country.code}>{country.name}</option>
                ))}
            </select>
            {heightSourceInfo && <div className="text-xs text-gray-500 mt-1 mb-1">{heightSourceInfo}</div>}
            <input 
                type="range" 
                id="personHeightSlider" 
                min={minVal} 
                max={maxVal} 
                step={stepVal} 
                value={getDisplayValue(displayHeight, unitSystem)}
                onInput={handleSliderInput}
                onChange={handleSliderChange}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer mt-1" 
            />
        </div>
    );
};

export default PersonHeightControl;
